import { useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Skeleton } from "primereact/skeleton";
import { TabPanel, TabView } from "primereact/tabview";

import { fetchExerciseStep } from "sj-ui-toolkit/src/api";
import { UsedHint } from "sj-ui-toolkit/src/api/types/experimentTypes";
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils";

import { useLearnContext } from "../../../../../context/LearnContext";
import { useTournamentContext } from "../../../../../context/TournamentContext";
import { VideoLessonDetails } from "../../VideoProvider";

import S from "./_styles.module.scss";

export default function ExerciseInstructions({
  lessonDetails,
}: {
  lessonDetails: VideoLessonDetails;
}) {
  const description = lessonDetails.experiment.description;
  const steps = lessonDetails.steps;
  const { isTournament } = useLearnContext();
  const { handleHintUsage } = useTournamentContext();
  const [hints, setHints] = useState<(string | undefined)[]>(Array.from({ length: steps.length }));
  const [nextHint, setNextHint] = useState(0);
  const [usedHints, setUsedHints] = useState<UsedHint[]>(
    Array.isArray(lessonDetails.experiment.usedHints) ? lessonDetails.experiment.usedHints : []
  );

  const onShowHint = async (idx: number) => {
    // Prevent fetching the same hint multiple times
    if (idx < nextHint) return;

    try {
      const step = steps[idx];
      const hintContent = await fetchExerciseStep(String(step));
      setNextHint(idx + 1);
      setHints(prevHints => {
        const newHints = [...prevHints];
        newHints[idx] = hintContent;
        return newHints;
      });
      if (isTournament && !usedHints.find(h => h.id === step)) {
        const pointCost = idx === hints.length - 1 ? 10 : 5;
        const data = await handleHintUsage(step, pointCost);
        setUsedHints(data?.usedHints ?? []);
      }
    } catch (error) {
      console.error("Error fetching hint content:", error);
    }
  };

  return (
    <div className={C(S.exerciseTextContainer)}>
      <TabView
        aria-label="Exercise content"
        pt={{
          navContainer: { className: C("sticky top-0 z-2", T.bgElevation0) },
          nav: { className: "atlas-h5" },
          inkbar: { className: T.bgGray300 },
          panelContainer: { className: C(T.bgElevation0, "p-0") },
        }}
      >
        <TabPanel header="Overview">
          <div className="pt-4">{description}</div>
        </TabPanel>
        {hints.length > 0 && (
          <TabPanel header="Hints">
            <div className="mt-4">
              <Accordion multiple onTabOpen={e => onShowHint(e.index)} aria-label="Hints">
                {hints.map((hint, idx) => (
                  <AccordionTab
                    key={idx}
                    disabled={idx > nextHint}
                    headerTemplate={
                      <AccordionTabHeader
                        idx={idx}
                        isTournament={isTournament}
                        hintUsed={usedHints.find(h => h.id === steps[idx])}
                        pointCost={idx === hints.length - 1 ? 10 : 5}
                      />
                    }
                  >
                    {idx <= nextHint && (
                      <pre className={S.hintContent}>{hint || <HintSkeleton />}</pre>
                    )}
                  </AccordionTab>
                ))}
              </Accordion>
            </div>
          </TabPanel>
        )}
      </TabView>
    </div>
  );
}

interface AccordionTabHeaderProps {
  idx: number;
  isTournament: boolean;
  hintUsed?: UsedHint;
  pointCost: number;
}

const AccordionTabHeader = ({
  idx,
  isTournament,
  hintUsed,
  pointCost,
}: AccordionTabHeaderProps) => {
  return (
    <div className="flex items-center gap-2 justify-between w-full">
      <span>Hint #{idx + 1}</span>
      {isTournament && !hintUsed && (
        <span className="text-primary-darker">-{pointCost} points</span>
      )}
    </div>
  );
};

const HintSkeleton = () => {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton width="100%" />
      <Skeleton width="75%" />
      <Skeleton width="90%" />
      <Skeleton width="80%" />
    </div>
  );
};
